/**
 * Lead List Component
 *
 * Server-side paginated list with the full legacy column set and the
 * complete filter catalog (quick search, field filters, catalogs, hotel
 * attributes, effective chain, tags/interests with AND-OR, date ranges).
 */

import React, { useState, useMemo, useContext, useCallback } from 'react';
import {
  BooleanInput,
  DateField,
  EditButton,
  FunctionField,
  List,
  NumberInput,
  TextField,
  useListContext,
  usePermissions,
  useTranslate,
} from 'react-admin';
import { Box, Button, Tooltip, useTheme } from '@mui/material';
import {
  FilterList as FilterListIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { CustomDatagrid } from '@/components/common';
import { compactListSx, containedListSx } from '@/components/common/compactListSx';
import { actionButtonSx, filterToggleSx } from '@/components/common/ListPageHeader';
import { CollapsibleFilterPanel, FilterExpandContext } from '@/components/common/CollapsibleFilterPanel';
import {
  CustomTextInputFilter,
  CustomSelectInputFilter,
  CustomDateInputFilter,
} from '@/components/common/CustomFilters';
import { httpClient } from '@/utils/httpClient';
import {
  LookupArrayFilter,
  FacetArrayFilter,
  FacetSelectFilter,
  useLeadFacets,
  CANONICAL_STATUSES,
} from './LeadFilterInputs';
import { LeadBulkActionButtons } from './LeadBulkActions';
import { NewsletterDialog } from './NewsletterDialog';

const NOTES_TRUNCATE_AT = 80;

type LeadRow = Record<string, unknown> & {
  notes?: string | null;
  tagNames?: string[];
  interestNames?: string[];
};

/**
 * Server-side CSV export honouring the active filters.
 */
const ExportButton = () => {
  const translate = useTranslate();
  const { filterValues, sort } = useListContext();

  const handleExport = useCallback(async () => {
    const params: Record<string, unknown> = { ...filterValues };
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (Array.isArray(value)) params[key] = value.join(',');
    });
    if (sort) {
      params.sortBy = sort.field;
      params.sortOrder = sort.order.toLowerCase();
    }
    const response = await httpClient.get<string>(
      `/leads/export?${queryString.stringify(params)}`
    );
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [filterValues, sort]);

  return (
    <Button
      onClick={handleExport}
      size="small"
      variant="contained"
      startIcon={<ExportIcon />}
      sx={actionButtonSx}
    >
      {translate('common.export')}
    </Button>
  );
};

const LeadActions = () => {
  const filterExpand = useContext(FilterExpandContext);
  const translate = useTranslate();
  const theme = useTheme();
  const navigate = useNavigate();
  const { permissions } = usePermissions<string>();
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  const canSendNewsletters = permissions === 'superuser' || permissions === 'marketing';

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Button
        onClick={() => filterExpand.setExpanded(!filterExpand.expanded)}
        size="small"
        variant={filterExpand.expanded ? 'contained' : 'outlined'}
        startIcon={<FilterListIcon sx={{ fontSize: '16px !important' }} />}
        sx={filterToggleSx(theme, filterExpand.expanded)}
      >
        {translate('common.filters.label')}
      </Button>
      <Button
        onClick={() => navigate('/leads/create')}
        size="small"
        variant="contained"
        startIcon={<AddIcon />}
        sx={actionButtonSx}
      >
        {translate('common.create')}
      </Button>
      <ExportButton />
      {canSendNewsletters && (
        <Button
          onClick={() => setNewsletterOpen(true)}
          size="small"
          variant="contained"
          startIcon={<SendIcon />}
          sx={actionButtonSx}
        >
          {translate('resources.leads.newsletter.button')}
        </Button>
      )}
      {newsletterOpen && (
        <NewsletterDialog open={newsletterOpen} onClose={() => setNewsletterOpen(false)} />
      )}
    </Box>
  );
};

export const LeadList = () => {
  const translate = useTranslate();
  const facets = useLeadFacets();

  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const filterExpandValue = useMemo(() => ({
    expanded: filtersExpanded,
    setExpanded: setFiltersExpanded,
  }), [filtersExpanded]);

  const statusChoices = useMemo(() => {
    const merged = [...CANONICAL_STATUSES];
    facets.statuses.forEach((s) => {
      if (!merged.includes(s)) merged.push(s);
    });
    return merged;
  }, [facets.statuses]);

  const t = useCallback(
    (key: string) => translate(`resources.leads.filters.${key}`),
    [translate]
  );
  const f = useCallback(
    (key: string) => translate(`resources.leads.fields.${key}`),
    [translate]
  );

  const modeChoices = useMemo(() => [
    { id: 'all', name: t('modeAll') },
    { id: 'any', name: t('modeAny') },
  ], [t]);

  const filters = useMemo(() => [
    <CollapsibleFilterPanel
      key="filters"
      source="_cfp"
      alwaysOn
      alwaysVisibleCount={{ xs: 1, sm: 2, md: 3 }}
      defaultExpanded={false}
    >
      <CustomTextInputFilter source="q" label={translate('common.search')} alwaysOn />
      <BooleanInput source="searchNotes" label={t('searchNotes')} helperText={false} />
      <CustomSelectInputFilter
        source="includeArchived"
        label={t('archived')}
        choices={[{ id: 'true', name: t('includeArchived') }]}
      />
      <CustomTextInputFilter source="firstName" label={f('firstName')} />
      <CustomTextInputFilter source="lastName" label={f('lastName')} />
      <CustomTextInputFilter source="company" label={t('company')} />
      <CustomTextInputFilter source="hotel" label={t('hotel')} />
      <CustomTextInputFilter source="city" label={f('city')} />
      <CustomTextInputFilter source="email" label={f('email')} />
      <CustomTextInputFilter source="emailDomainsInclude" label={t('emailDomainsInclude')} />
      <CustomTextInputFilter source="emailDomainsExclude" label={t('emailDomainsExclude')} />
      <FacetArrayFilter source="countries" label={t('countries')} values={facets.countries} />
      <FacetArrayFilter source="regions" label={t('regions')} values={facets.regions} />
      <LookupArrayFilter source="jobTitleIds" resource="job-titles" label={t('jobTitles')} />
      <FacetSelectFilter source="status" label={t('status')} values={statusChoices} />
      <FacetSelectFilter source="preferredLanguage" label={t('language')} values={facets.languages} />
      <CustomSelectInputFilter
        source="acceptsNewsletter"
        label={t('newsletter')}
        choices={[
          { id: 'true', name: t('newsletterYes') },
          { id: 'false', name: t('newsletterNo') },
        ]}
      />
      <LookupArrayFilter source="hotelChainIds" resource="hotel-chains" label={t('chains')} />
      <BooleanInput
        source="includeIndividualHotels"
        label={t('individualHotels')}
        helperText={false}
      />
      <NumberInput
        source="chainMinHotels"
        label={t('chainMinHotels')}
        helperText={false}
        size="small"
        sx={{ minWidth: 160 }}
      />
      <LookupArrayFilter source="hotelCategoryIds" resource="hotel-categories" label={t('hotelCategories')} />
      <LookupArrayFilter source="pmsSystemIds" resource="pms-systems" label={t('pmsSystems')} />
      <LookupArrayFilter source="lockSystemIds" resource="lock-systems" label={t('lockSystems')} />
      <LookupArrayFilter source="paymentProcessorIds" resource="payment-processors" label={t('paymentProcessors')} />
      <LookupArrayFilter source="tagIds" resource="tags" label={t('tags')} />
      <CustomSelectInputFilter source="tagMode" label={t('tagMode')} choices={modeChoices} />
      <LookupArrayFilter source="interestIds" resource="interests" label={t('interests')} />
      <CustomSelectInputFilter source="interestMode" label={t('interestMode')} choices={modeChoices} />
      <CustomDateInputFilter source="lastContactFrom" label={t('lastContactFrom')} />
      <CustomDateInputFilter source="lastContactTo" label={t('lastContactTo')} />
      <CustomDateInputFilter source="nextContactFrom" label={t('nextContactFrom')} />
      <CustomDateInputFilter source="nextContactTo" label={t('nextContactTo')} />
    </CollapsibleFilterPanel>,
  ], [translate, t, f, facets, statusChoices, modeChoices]);

  return (
    <FilterExpandContext.Provider value={filterExpandValue}>
      <List
        sx={[compactListSx, containedListSx] as any}
        filters={filters}
        actions={<LeadActions />}
        sort={{ field: 'lastName', order: 'ASC' }}
        perPage={25}
      >
        <CustomDatagrid
          primarySource="lastName"
          secondarySource="email"
          detailColumns={2}
          bulkActionButtons={<LeadBulkActionButtons />}
          sx={{
            '& .RaDatagrid-tableWrapper': { overflowX: 'auto' },
            '& .RaDatagrid-table': { minWidth: 1100 },
          }}
        >
          <TextField source="lastName" label={f('lastName')} />
          <TextField source="firstName" label={f('firstName')} />
          <TextField source="companyName" label={f('companyName')} sortBy="companyName" />
          <TextField source="hotelName" label={f('hotelName')} sortBy="hotelName" />
          <TextField source="jobTitleName" label={f('jobTitleName')} sortBy="jobTitleName" />
          <TextField source="effectiveChainName" label={f('effectiveChainName')} sortable={false} />
          <TextField source="email" label={f('email')} />
          <TextField source="mobilePhone" label={f('mobilePhone')} sortable={false} />
          <TextField source="city" label={f('city')} />
          <TextField source="country" label={f('country')} />
          <TextField source="status" label={f('status')} />
          <FunctionField
            label={f('tagNames')}
            render={(record: LeadRow) => (record.tagNames ?? []).join('; ')}
          />
          <DateField source="lastContactDate" label={f('lastContactDate')} sortBy="lastContactDate" />
          <EditButton />
        </CustomDatagrid>
      </List>
    </FilterExpandContext.Provider>
  );
};

export default LeadList;
